import appConfig from '../../config/appConfig';
import FactsheetDataModels from '@/api/FactsheetDataModels';
import AssetTypes from '../../api/AssetTypes';
import ClientsideLocations from '../../api/ClientsideLocations';

const geographyTemplate = {
  namespaced: true,
  state: {
    title: '',
    imageCaptionText: '',
    stateOverviewText: '',
    aboutCompanyText: '',
    primaryCompany: '',
    secondaryCompany: '',
    primaryLogo: '',
    secondaryLogo: '',
    primaryWebAddress: '',
    secondaryWebAddress: '',
    primaryImage: '',
    linkToFactSheet: '',
    promoInfo: '',
    promoInfoTypes: '',
    companyInfos: '',
    factoidsState: [{}],
    assetsState: [{}],
    profileLoaded: false,
    locationData: null,
    locationLoaded: false
  },
  mutations: {
    setTitle: (state, value) => {
      state.title = value;
    },
    setImageCaptionText: (state, value) => {
      state.imageCaptionText = value;
    },
    setStateOverviewText: (state, value) => {
      state.stateOverviewText = value;
    },
    setAboutCompanyText: (state, value) => {
      state.aboutCompanyText = value;
    },
    setPrimaryCompany: (state, value) => {
      state.primaryCompany = value;
    },
    setSecondaryCompany: (state, value) => {
      state.secondaryCompany = value;
    },
    setPrimaryLogo: (state, value) => {
      state.primaryLogo = value;
    },
    setSecondaryLogo: (state, value) => {
      state.secondaryLogo = value;
    },
    setPrimaryWebAddress: (state, value) => {
      state.primaryWebAddress = value;
    },
    setSecondaryWebAddress: (state, value) => {
      state.secondaryWebAddress = value;
    },
    setPrimaryImage: (state, value) => {
      state.primaryImage = value;
    },
    setLinkToFactSheet: (state, value) => {
      state.linkToFactSheet = value;
    },
    setpromoInfo: (state, value) => {
      state.promoInfo = value;
    },
    setPromoInfoTypes: (state, value) => {
      state.promoInfoTypes = value;
    },
    setCompanyInfos: (state, value) => {
      state.companyInfos = value;
    },
    setFactoidsState: (state, value) => {
      state.factoidsState = value;
    },
    setAssetsState: (state, value) => {
      state.assetsState = value;
    },
    setProfileLoaded: (state, value) => {
      state.profileLoaded = value;
    },
    setLocationData: (state, value) => {
      state.locationData = value;
    },
    setLocationLoaded: (state, value) => {
      state.locationLoaded = value;
    }
  },
  getters: {
    title: (state) => {
      return state.title;
    },
    imageCaptionText: (state) => {
      return state.imageCaptionText;
    },
    stateOverviewText: (state) => {
      return state.stateOverviewText;
    },
    aboutCompanyText: (state) => {
      return state.aboutCompanyText;
    },
    primaryCompany: (state) => {
      return state.primaryCompany;
    },
    secondaryCompany: (state) => {
      return state.secondaryCompany;
    },
    primaryLogo: (state) => {
      return state.primaryLogo;
    },
    secondaryLogo: (state) => {
      return state.secondaryLogo;
    },
    primaryWebAddress: (state) => {
      return state.primaryWebAddress;
    },
    secondaryWebAddress: (state) => {
      return state.secondaryWebAddress;
    },
    primaryImage: (state) => {
      return state.primaryImage;
    },
    linkToFactSheet: (state) => {
      return state.linkToFactSheet;
    },
    promoInfo: (state) => {
      return state.promoInfo;
    },
    promoInfoTypes: (state) => {
      return state.promoInfoTypes;
    },
    companyInfos: (state) => {
      return state.companyInfos;
    },
    factoidsState: (state) => {
      return state.factoidsState;
    },
    assetsState: (state) => {
      return state.assetsState;
    },
    profileLoaded: (state) => {
      return state.profileLoaded;
    },
    locationData: (state) => {
      return state.locationData;
    },
    locationLoaded: (state) => {
      return state.locationLoaded;
    }
  },
  actions: {
    initializeGeographyTemplate: ({ commit, state, rootGetters }) => {
      const stateDefaultProfile =
        rootGetters['profileConfig/stateDefaultProfile'];
      const countryData = rootGetters['mapTemplate/countryData'].find(
        (data) =>
          data.COUNTRY_NAME.toUpperCase() ===
          appConfig.globalFactsheet.toUpperCase()
      );
      const location = rootGetters['mapTemplate/selectedItemValue'];
      const profile = stateDefaultProfile.templates[0];

      const getValue = (value) =>
        location !== appConfig.globalFactsheet
          ? state.locationData[value]
          : countryData[value];

      const loadData = async () => {
        if (profile) {
          profile.title
            ? commit('setTitle', profile.title)
            : commit('setTitle', null);

          profile.imageCaptionTextField
            ? commit(
              'setImageCaptionText',
              await getValue(profile.imageCaptionTextField)
            )
            : commit('setImageCaptionText', null);

          profile.stateOverviewTextField
            ? commit(
              'setStateOverviewText',
              await getValue(profile.stateOverviewTextField)
            )
            : commit('setStateOverviewText', null);

          profile.aboutCompanyTextField
            ? commit(
              'setAboutCompanyText',
              await getValue(profile.aboutCompanyTextField)
            )
            : commit('setAboutCompanyText', null);

          profile.primaryCompanyField
            ? commit(
              'setPrimaryCompany',
              await getValue(profile.primaryCompanyField)
            )
            : commit('setPrimaryCompany', null);

          profile.secondaryCompanyField
            ? commit(
              'setSecondaryCompany',
              await getValue(profile.secondaryCompanyField)
            )
            : commit('setSecondaryCompany', null);

          profile.primaryLogoField
            ? commit('setPrimaryLogo', await getValue(profile.primaryLogoField))
            : commit('setPrimaryLogo', null);

          profile.secondaryLogoField
            ? commit(
              'setSecondaryLogo',
              await getValue(profile.secondaryLogoField)
            )
            : commit('setSecondaryLogo', null);

          profile.primaryWebAddressField
            ? commit(
              'setPrimaryWebAddress',
              getValue(profile.primaryWebAddressField)
            )
            : commit('setPrimaryWebAddressField', null);

          profile.secondaryWebAddressField
            ? commit(
              'setSecondaryWebAddress',
              getValue(profile.secondaryWebAddressField)
            )
            : commit('setSecondaryWebAddress', null);

          profile.primaryImageField
            ? commit(
              'setPrimaryImage',
              await getValue(profile.primaryImageField)
            )
            : commit('setPrimaryImage', null);

          profile.linkToFactSheetField
            ? commit(
              'setLinkToFactSheet',
              getValue(profile.linkToFactSheetField)
            )
            : commit('setLinkToFactSheet', null);

          profile.promoInfoField
            ? commit('setpromoInfo', getValue(profile.promoInfoField))
            : commit('setpromoInfo', null);

          profile.companyInfos
            ? commit('setCompanyInfos', profile.companyInfos)
            : commit('setCompanyInfos', null);

          profile.factoidsState
            ? commit('setFactoidsState', profile.factoidsState)
            : commit('setFactoidsState', null);

          profile.assetsState
            ? commit('setAssetsState', profile.assetsState)
            : commit('setAssetsState', null);

          const dataModelHelper = new FactsheetDataModels(
            appConfig.factSheetModelApiAxiosConfig
          );

          if (!profile.promoInfoTypes) {
            commit('setProfileLoaded', true);
            return;
          }
          const dataModelPromises = [];
          profile.promoInfoTypes.forEach((item) => {
            dataModelPromises.push(dataModelHelper.GetPromoBoxDetails(item));
          });
          const dataModelData = await Promise.all(dataModelPromises)
            .then((results) => {
              const promos = {};
              profile.promoInfoTypes.forEach((item, i) => {
                promos[item] = results[i];
              });
              commit('setPromoInfoTypes', promos);
            })
            .finally(() => {
              commit('setProfileLoaded', true);
            })
            .catch((err) => {
              console.error(err);
            });
        }
      };
      loadData();
    },
    async selectLocation(
      { commit, rootGetters, rootMutations, dispatch },
      location
    ) {
      let data;
      if (location === appConfig.globalFactsheet) {
        const countryData = rootGetters['mapTemplate/countryData'];
        data = countryData;
      } else {
        const locations = new ClientsideLocations(appConfig.locationApiProps);
        const locationFeatureLayer =
          rootGetters['mapTemplate/locationFeatureLayer'];
        data = await locations
          .getLocationData(location, locationFeatureLayer)
          .catch((err) => {
            console.log(err);
            commit('setError', err, {
              root: true
            });
          });
      }
      if (data) {
        commit('setLocationData', data);
        commit('setLocationLoaded', true);
        commit('mapTemplate/setSelectedItemType', 'Geography', { root: true });
        commit('mapTemplate/setSelectedItemValue', location, { root: true });
        dispatch('initializeGeographyTemplate');
      } else {
        commit('setLocationData', null);
        commit('setLocationLoaded', false);
      }
    }
  }
};

export default geographyTemplate;
